import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import TableSortLabel from "@mui/material/TableSortLabel";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import { actionSheetFileUpdated, selectorSheet } from "./spreadsheetRedux";
import { CollapseHeading } from "./SpreadsheetSettingSharedComponents";
import { actionMapZoomFeaturesUpdated } from "../Map";
import { actionAppBarFloorUpdated } from "../AppBar";
import { FILE_EXT_BY_MIME_TYPE } from "./spreadsheetutils.js";

const sectionTitle_ = (theme) => ({
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "10px",
  fontWeight: 500,
  "&:hover": {
    cursor: "pointer",
  },
  "& :hover": {
    cursor: "pointer",
  },
  "& button": {
    padding: "0px",
    marginLeft: "4px",
  },
});

const settingExpandHeader_ = (theme) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "10px",
  paddingLeft: "10px",
  fontStyle: "italic",
  "&:hover": {
    cursor: "pointer",
  },
  "& :hover": {
    cursor: "pointer",
  },
  "& button": {
    padding: "0px",
    marginLeft: "4px",
  },
});

const componentContainer_ = (theme) => ({
  paddingTop: "0px",
  paddingLeft: "15px",
  paddingRight: "15px",
  paddingBottom: "0px",
});

const hr_ = (theme) => ({
  backgroundColor: "lightgrey",
  height: 1,
  border: 0,
});

const buildingRow_ = (theme) => ({
  cursor: "pointer",
});

const hoverRow_ = (theme) => ({
  backgroundColor: "rgba(0, 0, 0, 0.04)",
});

const sheetRow_ = (theme) => ({
  "&:nth-of-type(2n)": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const dataHeader_ = (theme) => ({
  marginTop: "10px",
  marginBottom: "5px",
});

const formControl_ = (theme) => ({
  margin: "0px",
  minWidth: 80,
});

const selector_ = (theme) => ({
  minWidth: 200,
});

const selectRow_ = (theme) => ({
  width: "100%",
});

const flexRow_ = (theme) => ({
  display: "flex",
  justifyContent: "space-evenly",
  alignItems: "center",
});

const flexColumn_ = (theme) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-evenly",
  alignItems: "center",
});

const wordBreak_ = (theme) => ({
  wordBreak: "break-word",
});

const DISPLAY_BY_SOURCE_TYPE = {
  local: "Local Upload",
  google: "Google Drive",
};

const HeaderSelector = ({ sheetId }) => {
  const { rmrecnbr, lnglat, json, contentRmrecnbrExpanded } = useSelector(
    selectorSheet(sheetId),
  );
  const headerRow = json[0] || [];

  const dispatch = useDispatch();

  const lngRedux = lnglat && Array.isArray(lnglat) && lnglat[0];
  const latRedux = lnglat && Array.isArray(lnglat) && lnglat[1];

  const [rrnActive, setRrnActive] = useState(rmrecnbr !== null);
  const [lngLatActive, setLngLatActive] = useState(
    lngRedux !== null && latRedux !== null,
  );
  const [rrnVal, setRrnVal] = useState("");
  const [lngVal, setLngVal] = useState("");
  const [latVal, setLatVal] = useState("");

  useEffect(() => {
    const latSelectorVal = latRedux === null ? "" : latRedux;
    const lngSelectorVal = lngRedux === null ? "" : lngRedux;
    const rrnSelectorVal = rmrecnbr === null ? "" : rmrecnbr;
    setLatVal(latSelectorVal);
    setLngVal(lngSelectorVal);
    setRrnVal(rrnSelectorVal);
    setRrnActive(rmrecnbr !== null);
    setLngLatActive(lngRedux !== null && latRedux !== null);
  }, [lngRedux, latRedux, rmrecnbr]);

  const handleRrnSwitch = () => {
    setRrnActive(!rrnActive);
  };

  const handleLngLatSwitch = () => {
    setLngLatActive(!lngLatActive);
  };

  const handleRrnSelect = (e) => {
    setRrnVal(e.target.value);
  };

  const handleLngSelect = (e) => {
    setLngVal(e.target.value);
  };

  const handleLatSelect = (e) => {
    setLatVal(e.target.value);
  };

  const handleSubmit = () => {
    const rrnSubmit = rrnActive ? rrnVal : null;
    const lngLatSubmit = lngLatActive ? [lngVal, latVal] : null;
    let type;

    if (rrnActive && lngLatActive) type = "dual";
    else if (rrnActive) type = "room";
    else if (lngLatActive) type = "point";

    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        bbox: null,
        contentRmrecnbrExpanded: !contentRmrecnbrExpanded,
        rmrecnbr: rrnSubmit,
        lnglat: lngLatSubmit,
        type,
      }),
    );
  };

  return (
    <>
      <Box sx={flexRow_}>
        <Switch
          slotProps={{
            input: {
              "aria-label": "Toggle Room Record Number header selector",
            },
          }}
          checked={rrnActive}
          onChange={handleRrnSwitch}
        />
        <Box sx={[selector_, flexColumn_]}>
          <Box
            id="select-room-record-number"
            sx={selectRow_}
            style={{ opacity: rrnActive ? "100%" : "50%" }}
          >
            Room Record Number
          </Box>
          <FormControl
            variant="standard"
            disabled={!rrnActive}
            sx={[selectRow_, formControl_]}
            fullWidth
          >
            <Select
              variant="standard"
              labelId="select-room-record-number"
              id="select-layer-text"
              value={rrnVal}
              onChange={handleRrnSelect}
              displayEmpty
              required
            >
              <MenuItem value={""}>Unselected</MenuItem>
              {headerRow.map((x, i) => {
                return (
                  <MenuItem key={i} value={i}>
                    {x}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box sx={flexRow_} style={{ marginTop: "20px", marginBottom: "20px" }}>
        <Switch
          slotProps={{
            input: {
              "aria-label": "Toggle Longitude/Latitude header selectors",
            },
          }}
          checked={lngLatActive}
          onChange={handleLngLatSwitch}
        />
        <Box sx={[selector_, flexColumn_]}>
          <Box sx={selectRow_}>
            <Box
              id="select-longitude"
              sx={selectRow_}
              style={{ opacity: lngLatActive ? "100%" : "50%" }}
            >
              Longitude
            </Box>
            <FormControl
              variant="standard"
              disabled={!lngLatActive}
              sx={[selectRow_, formControl_]}
              fullWidth
            >
              <Select
                variant="standard"
                labelId="select-longitude"
                id="select-layer-text"
                value={lngVal}
                onChange={handleLngSelect}
                displayEmpty
                required
              >
                <MenuItem value={""}>Unselected</MenuItem>
                {headerRow.map((x, i) => {
                  return (
                    <MenuItem key={i} value={i}>
                      {x}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
          <Box sx={selectRow_} style={{ marginTop: "10px" }}>
            <Box
              id="select-latitude"
              sx={selectRow_}
              style={{ opacity: lngLatActive ? "100%" : "50%" }}
            >
              Latitude
            </Box>
            <FormControl
              variant="standard"
              disabled={!lngLatActive}
              sx={[selectRow_, formControl_]}
              fullWidth
            >
              <Select
                variant="standard"
                labelId="select-latitude"
                id="select-layer-text"
                value={latVal}
                onChange={handleLatSelect}
                displayEmpty
                required
              >
                <MenuItem value={""}>Unselected</MenuItem>
                {headerRow.map((x, i) => {
                  return (
                    <MenuItem key={i} value={i}>
                      {x}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
      <Box sx={flexRow_}>
        <Button variant="contained" onClick={handleSubmit}>
          Submit
        </Button>
      </Box>
    </>
  );
};

const SheetData = ({ sheetId }) => {
  const { json, fileName, source, sharedDataId } = useSelector(
    selectorSheet(sheetId),
  );
  const { sharedDataFilesArray } = useSelector(
    (state) => state.spreadsheetReducer,
  );
  const sharedDataFile = sharedDataFilesArray?.find(
    (file) => file.id === sharedDataId,
  );
  const { mimeType, parent, owner, modifiedTime } = sharedDataFile || {};

  const formattedModifiedTime =
    modifiedTime &&
    dayjs(modifiedTime).isValid() &&
    dayjs(modifiedTime).format("MMM D, YYYY h:mm A");

  return (
    <Box>
      <Box sx={wordBreak_}>File Name: {fileName}</Box>
      <Box sx={wordBreak_}>Spreadsheet Rows: {json && json.length - 1}</Box>
      <Box sx={wordBreak_}>
        Source: {DISPLAY_BY_SOURCE_TYPE[source] || "Unknown"}
      </Box>
      {owner && <Box sx={wordBreak_}>Owner: {owner}</Box>}
      {mimeType && (
        <Box sx={wordBreak_}>File Type: {FILE_EXT_BY_MIME_TYPE[mimeType]}</Box>
      )}
      {parent && <Box sx={wordBreak_}>Parent Folder: {parent}</Box>}
      {modifiedTime && (
        <Box sx={wordBreak_}>Last Modified: {formattedModifiedTime}</Box>
      )}
    </Box>
  );
};

const BuildingData = ({ sheetId }) => {
  const { buildingData } = useSelector(selectorSheet(sheetId));
  const dispatch = useDispatch();
  const [hoverRow, setHoverRow] = useState(null);
  const [sortBy, setSortBy] = useState("building"); // building, floor, totalSqFeet
  const [lastSortBy, setLastSortBy] = useState("floor"); // building, floor, totalSqFeet
  const [sort, setSort] = useState("asc"); // asc, desc
  const [lastSort, setLastSort] = useState("asc"); // asc, desc

  if (!buildingData) return "";

  const handleSortChange = (property) => {
    if (property === sortBy) {
      setSort(sort === "asc" ? "desc" : "asc");
    } else {
      setLastSort(sort);
      setLastSortBy(sortBy);
      setSortBy(property);
      setSort("desc");
    }
  };

  const sorted = (data, sort, sortBy, lastSort, lastSortBy) => {
    let sortable = data.map((x) => x);
    return sortable.sort((a, b) => {
      // primary sort
      if (a.data[sortBy] > b.data[sortBy]) return sort === "desc" ? -1 : 1;
      if (a.data[sortBy] < b.data[sortBy]) return sort === "desc" ? 1 : -1;
      // secondary sort
      if (a.data[lastSortBy] > b.data[lastSortBy])
        return lastSort === "desc" ? -1 : 1;
      if (a.data[lastSortBy] < b.data[lastSortBy])
        return lastSort === "desc" ? 1 : -1;
      return 0;
    });
  };

  const handleClick = (bbox, floor) => {
    dispatch(
      actionMapZoomFeaturesUpdated({
        type: "bbox",
        data: bbox,
      }),
    );
    dispatch(actionAppBarFloorUpdated(floor));
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortBy === "building"}
                direction={sort}
                onClick={() => handleSortChange("building")}
              >
                Building
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "floor"}
                direction={sort}
                onClick={() => handleSortChange("floor")}
              >
                Floor
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "totalSqFeet"}
                direction={sort}
                onClick={() => handleSortChange("totalSqFeet")}
              >
                Square Feet
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted(buildingData, sort, sortBy, lastSort, lastSortBy).map(
            (dataObj, index) => {
              return (
                <TableRow
                  onMouseEnter={() => setHoverRow(index)}
                  onMouseLeave={() => setHoverRow(null)}
                  onClick={() => handleClick(dataObj.bbox, dataObj.data.floor)}
                  key={index}
                  sx={[buildingRow_, index === hoverRow && hoverRow_]}
                >
                  <TableCell>{dataObj.data.building}</TableCell>
                  <TableCell>{dataObj.data.floor} </TableCell>
                  <TableCell>{dataObj.data.totalSqFeet}</TableCell>
                </TableRow>
              );
            },
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const SelectedRoom = ({ sheetId }) => {
  const {
    json,
    selectedRmrecnbr,
    selectedRmnbr,
    rmrecnbr: rmrecnbrCol,
    selectedPoint,
  } = useSelector(selectorSheet(sheetId));
  const rmrecnbr = selectedRmrecnbr || "";
  const rmnbr = selectedRmnbr || "";
  const point = selectedPoint;

  if (!rmrecnbr && !point)
    return <Typography>Click room on map to select.</Typography>;

  const headerRow = json && json[0];
  const selectedRows =
    json && rmrecnbr
      ? json.filter((row) => String(row[rmrecnbrCol]) === String(rmrecnbr))
      : [];
  const selectedPointRows = json && point ? [json[point]] : [];
  let displayTable = [];

  if (headerRow && (selectedRows.length > 0 || selectedPointRows.length > 0)) {
    for (let i = 0; i < headerRow.length; i++) {
      displayTable.push([
        headerRow[i],
        ...selectedRows.map((row) => row[i]),
        ...selectedPointRows.map((row) => row[i]),
      ]);
    }
  }

  return (
    <>
      {rmrecnbr ? (
        <>
          <Typography sx={dataHeader_}>Room Data:</Typography>
          <Box>Room Number: {rmnbr}</Box>
          <Box>Room Record Number: {rmrecnbr}</Box>
        </>
      ) : (
        ""
      )}
      {point ? <Typography sx={dataHeader_}>Point Data</Typography> : ""}
      <Typography sx={dataHeader_}>Spreadsheet Data:</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Column Header</TableCell>
              {displayTable.length > 0 &&
                new Array(displayTable[0].length - 1)
                  .fill(null)
                  .map((cell, index) => {
                    return <TableCell key={index}>Value</TableCell>;
                  })}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayTable.map((row, index) => {
              return (
                <TableRow key={index} sx={sheetRow_}>
                  {row.map((cell, index) => {
                    return <TableCell key={index}>{String(cell)}</TableCell>;
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default function Content({ sheetId }) {
  const {
    contentExpanded,
    contentRmrecnbrExpanded,
    contentSheetDataExpanded,
    contentBuildingDataExpanded,
    contentSelectedRoomExpanded,
  } = useSelector(selectorSheet(sheetId));

  const dispatch = useDispatch();

  const handleContentExpand = () => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        contentExpanded: !contentExpanded,
      }),
    );
  };

  const handleRmrecnbrExpand = () => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        contentRmrecnbrExpanded: !contentRmrecnbrExpanded,
      }),
    );
  };

  const handleSheetDataExpand = () => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        contentSheetDataExpanded: !contentSheetDataExpanded,
      }),
    );
  };

  const handleBuildingDataExpand = () => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        contentBuildingDataExpanded: !contentBuildingDataExpanded,
      }),
    );
  };

  const handleSelectedRoomExpand = () => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        contentSelectedRoomExpanded: !contentSelectedRoomExpanded,
      }),
    );
  };

  return (
    <>
      <CollapseHeading
        headingClass={sectionTitle_}
        expanded={contentExpanded}
        setExpanded={handleContentExpand}
        headingText="Content"
      />
      <Collapse in={contentExpanded}>
        <Paper>
          <CardContent>
            <CollapseHeading
              headingClass={settingExpandHeader_}
              expanded={contentRmrecnbrExpanded}
              setExpanded={handleRmrecnbrExpand}
              headingText="Header Columns"
            />
            <Collapse in={contentRmrecnbrExpanded}>
              <Box sx={componentContainer_}>
                <HeaderSelector sheetId={sheetId} />
              </Box>
            </Collapse>
            <Box component="hr" sx={hr_} />
            <CollapseHeading
              headingClass={settingExpandHeader_}
              expanded={contentSheetDataExpanded}
              setExpanded={handleSheetDataExpand}
              headingText="Spreadsheet Data"
            />
            <Collapse in={contentSheetDataExpanded}>
              <Box sx={componentContainer_}>
                <SheetData sheetId={sheetId} />
              </Box>
            </Collapse>
            <Box component="hr" sx={hr_} />
            <CollapseHeading
              headingClass={settingExpandHeader_}
              expanded={contentBuildingDataExpanded}
              setExpanded={handleBuildingDataExpand}
              headingText="Building Data"
            />
            <Collapse in={contentBuildingDataExpanded}>
              <Box sx={componentContainer_}>
                <BuildingData sheetId={sheetId} />
              </Box>
            </Collapse>
            <Box component="hr" sx={hr_} />
            <CollapseHeading
              headingClass={settingExpandHeader_}
              expanded={contentSelectedRoomExpanded}
              setExpanded={handleSelectedRoomExpand}
              headingText="Selected Data"
            />
            <Collapse in={contentSelectedRoomExpanded}>
              <Box sx={componentContainer_}>
                <SelectedRoom sheetId={sheetId} />
              </Box>
            </Collapse>
          </CardContent>
        </Paper>
      </Collapse>
    </>
  );
}
